import { lazy, memo, Suspense, useCallback, useEffect, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'

import FloatButton from '@hapana/shared/web/components/shared/float-button/float-button.component'
import { useDialog } from '@hapana/shared/web/components/shared/dialog/dialog.hooks'
import useSiteStore from '_stores/site.store'
import useCorporateStore from '_stores/corporate.store'

import {
  CHECK_IN_MODAL_PROPS,
  OPEN_CHECK_IN_WIDGET_EVENT,
} from '_components/check-in-widget/check-in-widget.constants'
import ModalCheckInSkeleton from '@components/check-in-widget/modal-check-in/modal-check-in-skeleton.component'
import { useSiteCheckInEvents } from '_components/check-in-widget/use-checkin-widget-events.hook'
import { useEquipmentFCMAlerts } from '_hooks/use-equipment-fcm-alerts'

const loadModalCheckIn = () =>
  import('@components/check-in-widget/modal-check-in/modal-check-in.component')
const LazyModalCheckIn = lazy(loadModalCheckIn)

// The check-in modal pulls in its whole tab tree (notes editor, equipment, add-on dialogs), so it
// loads on demand instead of in the entry bundle, and is prefetched once the page is idle (INT-1539).
const ModalCheckIn = (props) => (
  <Suspense fallback={<ModalCheckInSkeleton />}>
    <LazyModalCheckIn {...props} />
  </Suspense>
)

// Tab values inside ModalCheckIn, by the names the assistant uses in its links
const WIDGET_TAB_VALUES = { 'check-in': '1', waitline: '2', 'manage-equipment': '3' }

// Paths where the check-in button should be hidden
const HIDDEN_PATHS = ['/login', '/location-selection', '/select-location']

const CheckInFloatingButton = memo(() => {
  const { currentSite } = useSiteStore()
  const location = useLocation()
  const { pushDialog, closeDialog, dialogState } = useDialog()

  useEquipmentFCMAlerts()

  useEffect(() => {
    const prefetch = () => void loadModalCheckIn()
    if ('requestIdleCallback' in window) {
      const handle = window.requestIdleCallback(prefetch)
      return () => window.cancelIdleCallback(handle)
    }
    const timeout = setTimeout(prefetch, 2000)
    return () => clearTimeout(timeout)
  }, [])

  const [checkInCounter, setCheckInCounter] = useState(0)
  const [newCheckIn, setNewCheckIn] = useState({})

  const corporateId = useCorporateStore(
    (state) => state.corporate?.corporateBySubdomain?.corporateId
  )

  const dialogStateRef = useRef(dialogState)
  useEffect(() => {
    dialogStateRef.current = dialogState
  }, [dialogState])

  // OC-448: opening the widget "reads" the missed check-ins. Reset the unread badge to 0 whenever
  // it transitions to open, however it was opened (click, keyboard, or programmatically) — this is
  // what makes `checkInCounter` a "check-ins since last opened" count. Resetting the counter also
  // returns the icon to its default colour and hides the badge (FloatButton keys both off counter).
  useEffect(() => {
    if (dialogState?.open) {
      setCheckInCounter(0)
    }
  }, [dialogState?.open])

  // OC-448: real-time check-in events via Firestore (per-corporate tenant isolation), replacing the
  // FCM/BroadcastChannel transport for the widget. The launcher listens to the SITE feed, which
  // carries EVERY check-in source at the currently selected site — QR_CODE, CONTACTLESS, IN_APP,
  // FOB_SCAN, KIOSK, staff manual, barcode, Agent — not just barcode scans.
  //
  // Behaviour (changed): a check-in NO LONGER auto-opens the widget. Every check-in updates the
  // CLOSED launcher icon instead: it pulses, bumps the "unread since last opened" badge, and
  // recolours by status (green = success, red = fail). The operator opens the widget when they
  // choose; opening resets the badge (see the reset effect above). While the widget is open,
  // CheckInTab's own subscriptions handle live in-modal updates, so we skip counting those here.
  const handleWidgetEvent = useCallback((event) => {
    if (dialogStateRef.current?.open) {
      return
    }

    setNewCheckIn({
      checkInId: event.checkInId,
      status: event.status,
    })
    setCheckInCounter((prev) => prev + 1)
  }, [])

  useSiteCheckInEvents({ corporateId, siteId: currentSite?.siteId }, handleWidgetEvent)

  // OC-448: Cmd/Ctrl+Shift+U toggles the check-in widget open/closed. (Cmd/Ctrl+Shift+W was
  // rejected — it closes the browser window.) Verified free against the app's kbar bindings
  // (only Cmd/Ctrl+K and "/"). Only active where the floating button itself is shown.
  useEffect(() => {
    const isHiddenPath = HIDDEN_PATHS.some((path) => location.pathname.includes(path))
    if (!currentSite || isHiddenPath) {
      return undefined
    }

    const handleKeyDown = (event) => {
      if (event.repeat) {
        return
      }
      if ((event.metaKey || event.ctrlKey) && event.shiftKey && event.code === 'KeyU') {
        event.preventDefault()
        if (dialogStateRef.current?.open) {
          closeDialog()
        } else {
          setCheckInCounter(0)
          pushDialog(ModalCheckIn, CHECK_IN_MODAL_PROPS)
        }
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [currentSite, location.pathname, pushDialog, closeDialog])

  // The support assistant links to a widget tab by name; the tabs have no URL, so it asks this
  // button (the only owner of the widget dialog) to open the widget on that tab.
  useEffect(() => {
    if (!currentSite) {
      return undefined
    }
    const handleOpenRequest = (event) => {
      const defaultTab = WIDGET_TAB_VALUES[event.detail?.tab] ?? WIDGET_TAB_VALUES['check-in']
      setCheckInCounter(0)
      pushDialog(ModalCheckIn, { ...CHECK_IN_MODAL_PROPS, defaultTab })
    }
    window.addEventListener(OPEN_CHECK_IN_WIDGET_EVENT, handleOpenRequest)
    return () => window.removeEventListener(OPEN_CHECK_IN_WIDGET_EVENT, handleOpenRequest)
  }, [currentSite, pushDialog])

  // Only show when a site is selected
  if (!currentSite) {
    return null
  }

  // Hide on certain paths
  if (HIDDEN_PATHS.some((path) => location.pathname.includes(path))) {
    return null
  }

  const handleClick = () => {
    setCheckInCounter(0)
    pushDialog(ModalCheckIn, CHECK_IN_MODAL_PROPS)
  }

  // Hide when dialog is open to avoid overlap
  const isVisible = !dialogState?.open

  return (
    <FloatButton
      onClick={handleClick}
      counter={checkInCounter}
      newCheckIn={newCheckIn}
      isVisible={isVisible}
    />
  )
})

CheckInFloatingButton.displayName = 'CheckInFloatingButton'

export { CheckInFloatingButton }
export default CheckInFloatingButton
